import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SectorSection = ({ id, title, description, subsections, images, sectorLink, accentColor }) => {
  const navigate = useNavigate();
  
  const navigateToSubsection = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };
  
  const getAccentColor = () => {
    switch(accentColor) {
      case 'red': return '#8B1818';
      case 'blue': return '#000080';
      case 'gray': return '#6B7280';
      default: return '#8B1818';
    }
  };

  const getHoverColor = () => {
    switch(accentColor) {
      case 'red': return '#6B1212';
      case 'blue': return '#000066';
      case 'gray': return '#4B5563';
      default: return '#6B1212';
    }
  };

  return (
    <section id={id} className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{title}</h2>
          <div className="w-24 h-1 mx-auto" style={{ backgroundColor: getAccentColor() }}></div>
        </div>

        {/* Main Description with Image Grid */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div className="space-y-6">
            <p className="text-lg text-gray-700 leading-relaxed">{description}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {images.slice(0, 2).map((img, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-lg shadow-lg group">
                <img
                  src={img}
                  alt={`${title} ${idx + 1}`}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Subsections Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {subsections.map((subsection, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
              onClick={() => navigateToSubsection(`/${sectorLink}/${subsection.slug}`)}
            >
              <div className="relative overflow-hidden">
                <img
                  src={images[idx % images.length]}
                  alt={subsection.title}
                  className="w-full h-48 object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <h3 className="absolute bottom-4 left-4 text-white font-bold text-xl">{subsection.title}</h3>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 mb-4">{subsection.shortDescription}</p>
                <div className="flex items-center gap-2 font-semibold" style={{ color: getAccentColor() }}>
                  Learn More
                  <ArrowRight size={18} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SectorSection;